import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { subscriptionService } from '../../services/subscriptionService';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import InvoiceTable from './components/InvoiceTable';
import BillingOverview from './components/BillingOverview';
import PaymentMethodSection from './components/PaymentMethodSection';
import InvoiceDetailModal from './components/InvoiceDetailModal';

const BillingInvoices = () => {
  const { user, userProfile } = useAuth();
  const { subscription, hasActiveSubscription } = useSubscription();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (user?.id) {
      loadTransactions();
    }
  }, [user?.id]);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const { data } = await subscriptionService?.getPaymentTransactions(user?.id);
      setTransactions(data || []);
    } catch (error) {
      console.error('Failed to load transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!subscription?.stripeCustomerId) {
      alert('No subscription found');
      return;
    }

    try {
      const { data, error } = await subscriptionService?.createPortalSession(subscription?.stripeCustomerId);
      if (error) throw error;
      if (data?.url) {
        window.location.href = data?.url;
      }
    } catch (error) {
      console.error('Failed to open billing portal:', error);
      alert('Failed to open billing portal. Please try again.');
    }
  };

  const filteredTransactions = transactions?.filter(tx => {
    const matchesStatus = filterStatus === 'all' || tx?.status === filterStatus;
    const matchesSearch = searchQuery === '' || 
      tx?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      tx?.stripeInvoiceId?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  return (
    <MainLayout>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Billing & Invoices</h1>
            <p className="text-gray-600">Manage your subscription, payments, and invoice history</p>
          </div>

          {/* Billing Overview */}
          <BillingOverview 
            subscription={subscription}
            hasActiveSubscription={hasActiveSubscription}
            userProfile={userProfile}
            onManageSubscription={handleManageSubscription}
          />

          {/* Payment Method Section */}
          <PaymentMethodSection 
            subscription={subscription}
            onManageSubscription={handleManageSubscription}
          />

          {/* Invoice Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-1">Invoice History</h2>
                <p className="text-sm text-gray-600">View and download your payment invoices</p>
              </div>
              <button
                onClick={loadTransactions}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Icon name="RefreshCw" className="w-4 h-4" />
                Refresh
              </button>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex-1">
                <div className="relative">
                  <Icon name="Search" className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search invoices..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e?.target?.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="all">All Status</option>
                <option value="succeeded">Paid</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            {/* Invoice Table */}
            <InvoiceTable 
              transactions={filteredTransactions}
              loading={loading}
              onViewDetails={setSelectedInvoice}
            />
          </div>

          {/* Tax Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Icon name="FileText" className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Tax Information</h3>
                <p className="text-sm text-gray-600 mb-4">
                  All prices include applicable taxes. Tax rates are calculated based on your billing address.
                </p>
                <button
                  onClick={handleManageSubscription}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700"
                >
                  Update billing information →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <InvoiceDetailModal 
          invoice={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
        />
      )}
    </MainLayout>
  );
};

export default BillingInvoices;